import { Link, Navigate, useParams } from "react-router-dom";
import PageMeta from "@/components/PageMeta";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { useImportGuides, guideSteps } from "@/lib/importGuides";
import { ImportGuideSteps } from "@/components/ImportGuideSteps";

const TAB = { file: "file", url: "url", bookmarks: "bookmarks", screenshot: "screenshot" };

export default function ImportHelpApp() {
  const { app } = useParams();
  const data = useImportGuides();
  if (!data) return <div className="max-w-3xl mx-auto px-4 py-12 text-slate-500">Loading guide…</div>;
  const g = data.guides.find((x) => x.key === app);
  if (!g) return <Navigate to="/help/import" replace />;
  if (g.own_page) return <Navigate to={g.own_page} replace />;
  const url = `https://recipefiler.com/help/import/${g.key}`;
  const howto = {
    "@context": "https://schema.org", "@type": "HowTo", name: `How to import recipes from ${g.label} into RecipeFiler`,
    step: guideSteps(g, data.common).map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.title || `Step ${i + 1}`, text: s.body })),
  };
  return (
    <div className="max-w-3xl mx-auto px-4 py-12" data-testid="import-help-app-page">
      <PageMeta title={`How to import recipes from ${g.label} — step-by-step guide`}
                description={`Exactly how to export your recipes from ${g.label} and bring them into RecipeFiler, photos included, first time.`} canonical={url} />
      <BreadcrumbJsonLd items={[{ name: "Home", url: "https://recipefiler.com/" }, { name: "Import guides", url: "https://recipefiler.com/help/import" }, { name: g.label, url }]} />
      {/* eslint-disable-next-line react/no-danger */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howto) }} />
      <Link to="/help/import" className="overline mb-2 inline-block">← All import guides</Link>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-rf-ink leading-tight">Importing from {g.label}, step by step</h1>
      <div className="mt-8"><ImportGuideSteps guide={g} common={data.common} /></div>
      <div className="mt-10 flex flex-wrap gap-4 items-center">
        <Link to={`/import?tab=${TAB[g.method]}`} className="btn-amber inline-flex" data-testid="import-help-start">Start your {g.label} import</Link>
        <Link to={`/${g.landing}`} className="text-sm font-semibold text-rf-orange underline">More about importing from {g.label}</Link>
      </div>
    </div>
  );
}
